
import { defineComponent, ref, computed, reactive, PropType } from 'vue'
import FactorItem from './FactorItem.vue'
import BoardSelectorPopup from './BoardSelectorPopup.vue'
import starApi from '@/api/starApi'

const BOARD_KEYWORDS = ['行业', '概念', '题材', '地区']

export default defineComponent({
  name: 'StockRangeSelector',
  components: { FactorItem, BoardSelectorPopup },
  props: {
    rangeData: { type: Array as PropType<any[]>, default: () => [] },
    isLight: Boolean,
    colors: { type: Object as PropType<Record<string, string>>, required: true },
    headers: { type: Object as PropType<Record<string, string>>, default: () => ({}) }
  },
  emits: ['change'],
  setup(props, { emit }) {
    const selected = ref<any[]>([])
    const selectedCodes = computed(() => new Set(selected.value.map((s: any) => s.attrCode)))

    // 板块已选：{ [itemAttrCode]: selectedOptions[] }
    const boardSelected = reactive<Record<string, any[]>>({})

    // 板块弹框状态
    const boardPopup = reactive({
      itemCode: '',
      options: [] as any[],
      loading: false
    })

    const rangeTree = computed(() => props.rangeData || [])

    const isBoardItem = (item: any) =>
      BOARD_KEYWORDS.some(kw => item.attrName?.includes(kw))

    // 普通 tag normalize，注入已选 selectedOptions
    const normalizeItem = (item: any) => {
      const base = item.attrType == null ? { ...item, attrType: 1 } : item
      if (base.attrType === 3) {
        const found = selected.value.find((s: any) => s.attrCode === base.attrCode)
        return { ...base, selectedOptions: found?.selectedOptions || [] }
      }
      return base
    }

    const tagStyle = (isSelected: boolean) => ({
      background: isSelected ? '#FFEDED' : (props.isLight ? '#f0f0f0' : '#2a2a2a'),
      color: isSelected ? '#1a1a1a' : props.colors.textPrimary,
      borderColor: 'transparent'
    })

    const openBoard = async (item: any) => {
      boardPopup.itemCode = item.attrCode
      boardPopup.options = []
      boardPopup.loading = true
      try {
        const res = await starApi.getStockFactorOptions({
          data: { factorCode: item.attrCode },
          headers: props.headers
        })
        const raw: any[] = res?.data || []
        boardPopup.options = raw.map((o: any) => ({
          attrCode: o.plateCode ?? o.optionCode ?? o.attrCode,
          attrName: o.plateName ?? o.optionName ?? o.attrName,
          changeRate: o.pxChangeRate ?? o.changeRate,
          market: o.market,
          firstLetter: o.firstLetter ?? ''
        }))
      } catch (e) { /* 静默 */ }
      finally { boardPopup.loading = false }
    }

    const onBoardConfirm = (itemCode: string, opts: any[]) => {
      boardSelected[itemCode] = opts
      boardPopup.itemCode = ''
      emitChange()
    }

    const clearBoard = (code: string) => {
      boardSelected[code] = []
      emitChange()
    }

    const isItemSelected = (item: any) => {
      if (item.attrType === 7) {
        return selectedCodes.value.has(item.attrCode)
      }
      if (item.attrType === 3) {
        const found = selected.value.find((s: any) => s.attrCode === item.attrCode)
        return !!(found?.selectedOptions?.length)
      }
      return selectedCodes.value.has(item.attrCode)
    }

    const getSelectedHint = (item: any) => {
      if (item.attrType !== 3) return ''
      const found = selected.value.find((s: any) => s.attrCode === item.attrCode)
      return (found?.selectedOptions || []).map((o: any) => o.attrName).join('、')
    }

    const groupSelectedSummary = (group: any) => {
      let total = 0
      for (const c of (group.childAttrList || [])) {
        if (isBoardItem(c)) {
          total += boardSelected[c.attrCode]?.length || 0
        } else if (c.attrType === 3) {
          const found = selected.value.find((s: any) => s.attrCode === c.attrCode)
          total += found?.selectedOptions?.length || 0
        } else if (selectedCodes.value.has(c.attrCode)) {
          total++
        }
      }
      return total > 0 ? `已选${total}个` : ''
    }

    const onToggle = (item: any) => {
      const idx = selected.value.findIndex((s: any) => s.attrCode === item.attrCode)
      if (item.attrType === 7 || item._confirmed) {
        // 区间类型：始终更新不删除
        if (idx >= 0) selected.value.splice(idx, 1, item)
        else selected.value.push(item)
      } else if (item.attrType === 3) {
        if (item.selectedOptions?.length) {
          if (idx >= 0) selected.value.splice(idx, 1, item)
          else selected.value.push(item)
        } else {
          if (idx >= 0) selected.value.splice(idx, 1)
        }
      } else {
        if (idx >= 0) selected.value.splice(idx, 1)
        else selected.value.push(item)
      }
      emitChange()
    }

    const emitChange = () => {
      // 板块：每个父因子作为一条记录，选中的选项放在 selectedOptions 里
      const boardItems = Object.entries(boardSelected)
        .filter(([, opts]) => opts.length > 0)
        .map(([itemCode, opts]) => {
          // 从 rangeTree 里找到对应的父因子信息
          const parentItem = rangeTree.value
            .flatMap((g: any) => g.childAttrList || [])
            .find((item: any) => item.attrCode === itemCode)
          return {
            attrCode: itemCode,
            attrName: parentItem?.attrName || itemCode,
            attrType: 5, // 板块是下拉多选类型
            selectedOptions: opts,
            _isBoard: true
          }
        })
      emit('change', [...selected.value, ...boardItems])
    }

    return {
      rangeTree, selected, selectedCodes, boardSelected, boardPopup,
      isBoardItem, normalizeItem, tagStyle,
      openBoard, onBoardConfirm, clearBoard,
      isItemSelected, getSelectedHint, groupSelectedSummary, onToggle
    }
  }
})
